/**
 * The battle pages' default copy (the rule is in `shared.ts`): the words the lobby, building one
 * and a battle's room all say of a battle's seats, tags and rounds, then each page's own, by
 * section. The claim's words and the refusals stay with the storefront's room, and what the room
 * states of its rolls is the platform's (`battleFairnessReadout`).
 */
import {
  type BattleMode,
  type BattleStatus,
  type BattleSummary,
  MAX_BATTLE_ROUNDS,
} from "../battles.js";
import { capitalizeTerm, type StorefrontTerminology } from "../storefrontTerminology.js";
import { buyCurrencyLabel, STOREFRONT_PAGING_COPY } from "./shared.js";

/** A mode's words: its name, how it is won, and how a decided battle says who took it all. */
export interface BattleModeCopy {
  label: string;
  /** The rule: a tag's hint, and the create page's description of the option. */
  rule: string;
  /** Over a decided battle's headline. */
  takesAll: string;
}

/**
 * Each mode's words: the tags on all three battle pages, the create page's options, the room's
 * outcome and the account's history. They name no term, so a constant serves every store's
 * default copy.
 */
export const BATTLE_MODE_COPY: Record<BattleMode, BattleModeCopy> = {
  standard: {
    label: "Standard",
    rule: "Highest total value wins everything",
    takesAll: "Highest total takes it all",
  },
  crazy: {
    label: "Crazy",
    rule: "Lowest total value wins everything",
    takesAll: "Lowest total takes it all",
  },
};

/** A battle's length: "1 round", "4 rounds". */
export function battleRoundsLabel(count: number): string {
  return `${count} ${count === 1 ? "round" : "rounds"}`;
}

/** The lobby's name: its heading, and the way back to it from the other battle pages. */
function lobbyLabel(terms: StorefrontTerminology): string {
  return capitalizeTerm(terms.battle.plural);
}

/** A round of a battle, counted from 1. */
function roundOfLabel(round: number, total: number): string {
  return `Round ${round} of ${total}`;
}

/** A word on a battle's head; the caller picks its tone. */
export interface BattleTagCopy {
  key: "mode" | "private" | "fastSpin";
  label: string;
  /** What the tag means, read on hover and by a screen reader. */
  hint?: string;
}

/** What every battle page calls a battle's parts: its seats, its tags and its rounds. */
export interface BattleCopy {
  /** The lobby's name, as each battle page's way back to it. */
  lobby: string;
  /** A seat: the viewer's own, a bot's, an empty one, and a player with no name. */
  seats: { you: string; bot: string; empty: string; player: string };
  /** A player seat's way to their profile: the viewer's own (null), or another's by name. */
  profileLabel: (name: string | null) => string;
  /** The mode with its rule, then Private and Fast spin where they apply. */
  tags: (battle: Pick<BattleSummary, "mode" | "isPublic" | "fastSpin">) => BattleTagCopy[];
  /** A running battle's progress, its round counted from 1. */
  roundOf: (round: number, total: number) => string;
  /** A round's box in the strip of rounds, counted from 0. */
  roundBox: (index: number, name: string) => string;
  /** A box the page can't name. */
  unknownBox: string;
}

export function defaultBattleCopy(terms: StorefrontTerminology): BattleCopy {
  return {
    lobby: lobbyLabel(terms),
    seats: { you: "You", bot: "Bot", empty: "Empty seat", player: "Player" },
    profileLabel: (name) => (name === null ? "View your profile" : `View ${name}'s profile`),
    tags: ({ mode, isPublic, fastSpin }) => {
      const tags: BattleTagCopy[] = [
        { key: "mode", label: BATTLE_MODE_COPY[mode].label, hint: BATTLE_MODE_COPY[mode].rule },
      ];
      if (!isPublic) {
        tags.push({
          key: "private",
          label: "Private",
          hint: "Only people with the link can join.",
        });
      }
      if (fastSpin) tags.push({ key: "fastSpin", label: "Fast spin" });
      return tags;
    },
    roundOf: roundOfLabel,
    roundBox: (index, name) => `Round ${index + 1}: ${name}`,
    unknownBox: terms.box.singular,
  };
}

/** The lobby's words, by section, in the store's terms. */
export interface BattlesLobbyCopy {
  heading: {
    title: string;
    description: string;
    /** Signed in: the way to build one. */
    create: string;
    /** Signed out: the way to sign in first. */
    signInToCreate: string;
    /** A short line under the title where the layout has room for one line only. */
    tagline: string;
    /** The way to the shopper's own battles, and that view's title. */
    history: string;
    historyTitle: string;
    /** The button that explains the modes, then its dialog's intro, the modes' title and close. */
    howItWorks: { label: string; intro: string; modesTitle: string; done: string };
  };
  /**
   * The chips' accessible name, and each chip: every battle, or the shopper's own. Then the
   * toolbar's price sort and modes filter.
   */
  filter: {
    label: string;
    options: { all: string; mine: string };
    sort: string;
    modes: { label: string; all: string };
  };
  list: {
    /** Names the list's region. */
    label: string;
    /** Each group's heading, by its battles' status. */
    groups: Record<BattleStatus, string>;
    /** Each card's way in, by its battle's status. */
    actions: Record<BattleStatus, string>;
    /** A finished battle's way in on the shopper's own history: its choreography again. */
    replay: string;
    /** A card's title: who plays whom ("1v1v1"). */
    matchup: (playerCount: number) => string;
    seatsFilled: (filled: number, seats: number) => string;
    /** Why the list shows nothing: the lobby has no battles, or the shopper has joined none. */
    empty: { all: string; mine: string };
    /** Nothing in the modes picked, and the way back to every mode. */
    emptyFiltered: string;
    showAllModes: string;
    /** The shopper's own battles while signed out, and the way to sign in. */
    signedOut: { message: string; action: string };
    /** Shows more of what is already loaded. */
    more: string;
    /** Fetches the next page of the shopper's own. */
    loadMore: string;
  };
}

export function defaultBattlesLobbyCopy(terms: StorefrontTerminology): BattlesLobbyCopy {
  const { box, item, battle } = terms;
  return {
    heading: {
      title: lobbyLabel(terms),
      description: `Same ${box.plural}, same rounds, one winner takes every ${item.singular}. Every roll is provably fair.`,
      create: `Create ${battle.singular}`,
      signInToCreate: `Sign in to create ${battle.plural}`,
      tagline: `Open ${box.plural} head to head`,
      history: `My ${battle.plural}`,
      historyTitle: `${capitalizeTerm(battle.singular)} history`,
      howItWorks: {
        label: `How ${battle.plural} work`,
        intro: `Take a seat, and everyone opens the same ${box.plural}, one each round. When the last round is open, the mode decides who wins every ${item.singular}.`,
        modesTitle: "Modes",
        done: "Got it",
      },
    },
    filter: {
      label: `Which ${battle.plural} to show`,
      options: { all: `All ${battle.plural}`, mine: `My ${battle.plural}` },
      sort: "Price",
      modes: { label: "Modes", all: "All modes" },
    },
    list: {
      label: lobbyLabel(terms),
      groups: { created: "Open lobbies", started: "In progress", completed: "Finished" },
      actions: { created: "Join", started: "Watch", completed: "Results" },
      replay: "Replay",
      matchup: (playerCount) =>
        Array.from({ length: Math.max(1, playerCount) }, () => "1").join("v"),
      seatsFilled: (filled, seats) => `${filled}/${seats} seats`,
      empty: {
        all: `No ${battle.plural} yet — create one and invite a friend, or start against bots.`,
        mine: `You haven't joined any ${battle.plural} yet.`,
      },
      emptyFiltered: `No ${battle.plural} in these modes right now. Try others, or start one of your own.`,
      showAllModes: "Show all modes",
      signedOut: { message: `Sign in to see your ${battle.plural}.`, action: "Sign in" },
      more: "See more",
      loadMore: STOREFRONT_PAGING_COPY.loadMore,
    },
  };
}

/** The create page's switches. */
export type BattleCreateToggle = "private" | "fastSpin" | "startWithBots";

/** How the arcade's box picker can order the store's boxes. */
export const BATTLE_PICKER_SORTS = [
  "priceDesc",
  "priceAsc",
  "volatilityDesc",
  "volatilityAsc",
] as const;
export type BattlePickerSort = (typeof BATTLE_PICKER_SORTS)[number];

/** The arcade layout's own words on the create page: its toolbar, its picker, its phone options. */
export interface BattleCreateArcadeCopy {
  /** The h1, and the main button once boxes are picked; with none it reads `addBoxes`. */
  title: string;
  submit: string;
  addBoxes: string;
  /** The phone's mode setting's title. */
  mode: string;
  /** The phone's collapsed switches, and their summary while none is on. */
  options: { title: string; off: string };
  /** The phone's bar under the cost: the rounds picked of the most a battle takes. */
  roundsOf: (rounds: number) => string;
  picks: {
    rounds: string;
    cost: string;
    sort: string;
    randomize: string;
    /** A picked box's art, which shows what is in it. */
    inspect: (name: string) => string;
    /** A picked box's handle, which drags it to another place in the order. */
    move: (name: string) => string;
    /** What a screen reader hears about dragging: how to, then each step, by name and place. */
    drag: {
      instructions: string;
      start: (name: string) => string;
      over: (name: string, place: number) => string;
      end: (name: string, place: number) => string;
      cancel: (name: string) => string;
    };
  };
  picker: {
    search: { label: string; placeholder: string };
    /** The price filter's name, every price, and a range's words from its formatted bounds. */
    price: {
      label: string;
      all: string;
      under: (max: string) => string;
      between: (min: string, max: string) => string;
      over: (min: string) => string;
    };
    sort: { label: string; options: Record<BattlePickerSort, string> };
    volatility: string;
    /** The house's boxes, shoppers' forge boxes, and the shopper's favorites. */
    tabs: { all: string; customs: string; favorites: string };
    empty: { title: string; body: string; favoritesTitle: string; favoritesBody: string };
    /** The phone's Filters sheet: its button, title, and how it clears and applies. */
    filters: { label: string; title: string; clear: string; apply: string };
    /** The footer: the picked rounds, the entry, and the button that adds them. */
    total: string;
    cost: string;
    add: string;
    /** A box's own Add, which stages one round of it. */
    addOne: string;
    favorite: (name: string) => string;
  };
  /** The contents dialog's way to the box's own page. */
  goTo: (name: string) => string;
}

/** The create page's words, by section, in the store's terms and currency. */
export interface BattleCreateCopy {
  /** The page's place under the lobby, its h1, and how a battle is built. */
  heading: { current: string; title: string; description: string };
  picks: {
    /** The picks' heading, with the battle's length. */
    title: (rounds: number) => string;
    hint: string;
    /** Said while nothing is picked. */
    empty: string;
    /** The tile that opens the picker, then the picker's title, description and close. */
    add: { label: string; title: string; description: string; done: string };
    /** A box's place among the picks (counted from 0), its stepper and its controls, by name. */
    pick: {
      position: (index: number) => string;
      rounds: (name: string) => string;
      fewer: (name: string) => string;
      more: (name: string) => string;
      earlier: (name: string) => string;
      later: (name: string) => string;
      remove: (name: string) => string;
    };
  };
  settings: {
    players: string;
    mode: string;
    /** Each switch's name, and what it does. */
    toggles: Record<BattleCreateToggle, { label: string; hint: string }>;
  };
  arcade: BattleCreateArcadeCopy;
  entry: {
    title: string;
    summary: (rounds: number, seats: number) => string;
    /** Beside a shortfall, where the store sells its currency: the link to Add funds. */
    buy: string;
    /** The action, with the formatted entry. */
    submit: (price: string) => string;
    submitting: string;
    cancel: string;
  };
}

export function defaultBattleCreateCopy({
  terms,
  currencyPlural,
}: {
  terms: StorefrontTerminology;
  /** The currency's plural name. */
  currencyPlural: string;
}): BattleCreateCopy {
  const { box, battle } = terms;
  return {
    heading: {
      current: "Create",
      title: `New ${battle.singular}`,
      description: `Pick the ${box.plural} and how many times each opens. Every seat pays the same entry and opens the same rounds.`,
    },
    picks: {
      title: (rounds) =>
        `${capitalizeTerm(box.plural)} in this ${battle.singular} · ${battleRoundsLabel(rounds)}`,
      hint: `Quantity is how many rounds of that ${box.singular}, up to ${MAX_BATTLE_ROUNDS} rounds in all. ${capitalizeTerm(box.plural)} open in this order, each for its quantity of rounds.`,
      empty: `No ${box.plural} yet: add one to start.`,
      add: {
        label: `Add ${box.singular}`,
        title: `Add ${box.plural}`,
        description: `Each one you add is one round of that ${box.singular}.`,
        done: "Done",
      },
      pick: {
        position: (index) => `${index + 1}.`,
        rounds: (name) => `Rounds of ${name}`,
        fewer: (name) => `One fewer round of ${name}`,
        more: (name) => `One more round of ${name}`,
        earlier: (name) => `Open ${name} earlier`,
        later: (name) => `Open ${name} later`,
        remove: (name) => `Remove ${name}`,
      },
    },
    settings: {
      players: "Players",
      mode: "Mode",
      toggles: {
        private: {
          label: "Private",
          hint: "Only people with the link can join; other shoppers never see it in the lobby.",
        },
        fastSpin: { label: "Fast spin", hint: "Shorter rounds." },
        startWithBots: {
          label: "Start with bots",
          hint: "Fill every other seat with a bot and start at once.",
        },
      },
    },
    arcade: {
      title: `Create ${battle.singular}`,
      submit: `Create ${battle.singular}`,
      addBoxes: `Add ${box.plural}`,
      mode: `${capitalizeTerm(battle.singular)} mode`,
      options: { title: `${capitalizeTerm(battle.singular)} options`, off: "All off" },
      roundsOf: (rounds) => `${rounds} of ${MAX_BATTLE_ROUNDS} rounds`,
      picks: {
        rounds: "Rounds",
        cost: "Cost",
        sort: "Price",
        randomize: "Randomize",
        inspect: (name) => `See what's in ${name}`,
        move: (name) => `Move ${name}`,
        drag: {
          instructions:
            "Press space to pick it up, the arrow keys to move it, then space to drop it or escape to cancel.",
          start: (name) => `Picked up ${name}.`,
          over: (name, place) => `${name} moved to place ${place}.`,
          end: (name, place) => `${name} dropped in place ${place}.`,
          cancel: (name) => `Moving ${name} canceled.`,
        },
      },
      picker: {
        search: { label: `Search ${box.plural}`, placeholder: `Search ${box.plural}` },
        price: {
          label: "Price",
          all: "All prices",
          under: (max) => `Under ${max}`,
          between: (min, max) => `${min} – ${max}`,
          over: (min) => `${min} and up`,
        },
        sort: {
          label: "Sort",
          options: {
            priceDesc: "Highest price",
            priceAsc: "Lowest price",
            volatilityDesc: "Most volatile",
            volatilityAsc: "Least volatile",
          },
        },
        volatility: "Volatility",
        tabs: { all: capitalizeTerm(box.plural), customs: "Customs", favorites: "Favorites" },
        empty: {
          title: `No ${box.plural} match`,
          body: "Try another name, price or volatility.",
          favoritesTitle: "No favorites yet",
          favoritesBody: `Heart ${box.plural} to keep them here.`,
        },
        filters: { label: "Filters", title: "Filters", clear: "Clear", apply: "Apply" },
        total: `Total ${box.plural}`,
        cost: `${capitalizeTerm(battle.singular)}'s cost`,
        add: "Add",
        addOne: `Add ${box.singular}`,
        favorite: (name) => `Favorite ${name}`,
      },
      goTo: (name) => `Go to ${name}`,
    },
    entry: {
      title: "Entry",
      summary: (rounds, seats) => `${battleRoundsLabel(rounds)} · ${seats} seats`,
      buy: buyCurrencyLabel(currencyPlural),
      submit: (price) => `Create for ${price}`,
      submitting: "Creating…",
      cancel: "Cancel",
    },
  };
}

/** A battle's room's words, by section, in the store's terms. */
/** The arcade battle room's words. */
export interface BattleRoomArcadeCopy {
  /** "Standard mode battle". */
  name: (modeLabel: string) => string;
  /** A phone's line. */
  cost: string;
  roundsShort: string;
  /** The bar's and the details' cost. */
  battleCost: string;
  fastSpin: string;
  private: string;
  mute: string;
  unmute: string;
  more: string;
  details: string;
  close: string;
  players: string;
  battleType: string;
  freeForAll: string;
  /** How many seats and the seating ("4 · 4-way"). */
  seating: (players: number) => string;
  /** The details' boxes, each once, and how many rounds open one ("×2"). */
  boxes: (count: number) => string;
  roundsOf: (count: number) => string;
  /** The stage's tab through the battle. */
  waitingTab: string;
  startingTab: string;
  finishedTab: string;
  waiting: string;
  ready: string;
  callAllBots: string;
  /** The countdown: a value for screen readers, then the word drawn at its end. */
  startingIn: (count: number) => string;
  go: string;
  goSaid: string;
  /** The tally's line while it counts, then who won (the result's title too), or how a tie on value was decided. */
  tallying: (round: number, total: number) => string;
  wins: (name: string, you: boolean) => string;
  /** How a tie on value was decided: by the rolls (higher wins, or lower in crazy), or seat order. */
  tiedOnValue: (by: "higherRolls" | "lowerRolls" | "seatOrder") => string;
  winner: string;
  recreate: string;
  rewatch: string;
  pulls: string;
  /** The phones' Pulls sheet: a player's own (its list's name, a seat's total's button). */
  pullsOf: (name: string) => string;
  noPulls: string;
  /** A player who takes nothing away once it is over. */
  noWinnings: string;
  /** A round's box's contents' way to the box's own page. */
  goTo: (name: string) => string;
}

export interface BattleRoomCopy {
  header: {
    /** The h1: how many play. */
    title: (playerCount: number) => string;
    /** The info bar's count of rounds, and the strip of rounds' name. */
    rounds: string;
    entry: string;
    copyLink: string;
  };
  /** Before it starts: each seat (counted from 0), the ways to fill one, and starting early. */
  seats: {
    seat: (index: number) => string;
    signInToJoin: string;
    join: string;
    addBot: string;
    /** How many seats are still empty. */
    waiting: (empty: number) => string;
    /** The creator's start, and anyone's once the lobby is stale. */
    startNow: string;
    start: string;
  };
  /** While it runs: the round showing (counted from 1) with its box when known, and a replay. */
  stage: {
    round: (round: number, total: number, boxName: string | null) => string;
    replay: string;
  };
  outcome: {
    /** After the winner's name: "You win", "mira wins". */
    win: string;
    wins: string;
    /** In place of a winner's name. */
    finished: string;
    /** What the winner pulled, when known, and what they take, both formatted. */
    summary: (amounts: { pulled: string | null; pot: string }) => string;
    replay: string;
    back: string;
  };
  results: {
    /** Names the region. */
    label: string;
    winner: string;
    /** A round still to show, counted from 0, numbered as boxed numbers it ("Round 01"). */
    placeholder: (index: number) => string;
  };
  /** The arcade room's own words: its head, stage, tally and result. */
  arcade: BattleRoomArcadeCopy;
  /** The fairness section's own words; what it states of the rolls is the platform's. */
  fairness: {
    title: string;
    show: string;
    hide: string;
    /** The opens table's caption, read by a screen reader. */
    caption: string;
    /** Said in place of the table while no round is revealed. */
    empty: string;
    /** The link to the raw verification data. */
    verification: string;
  };
}

export function defaultBattleRoomCopy(terms: StorefrontTerminology): BattleRoomCopy {
  const { item, battle, box } = terms;
  return {
    header: {
      title: (playerCount) => `${playerCount}-player ${battle.singular}`,
      rounds: "Rounds",
      entry: "Entry",
      copyLink: "Copy link",
    },
    seats: {
      seat: (index) => `Seat ${index + 1}`,
      signInToJoin: "Sign in to join",
      join: "Join seat",
      addBot: "Add bot",
      waiting: (empty) => `Waiting for ${empty} more ${empty === 1 ? "player" : "players"}…`,
      startNow: "Start now with bots",
      start: "Start with bots",
    },
    stage: {
      round: (round, total, boxName) =>
        `${roundOfLabel(round, total)}${boxName !== null ? ` · ${boxName}` : ""}`,
      replay: "Replay",
    },
    outcome: {
      win: "win",
      wins: "wins",
      finished: "Finished",
      summary: ({ pulled, pot }) =>
        `${pulled !== null ? `${pulled} pulled · ` : ""}every ${item.singular} worth ${pot} goes to the winner`,
      replay: "Watch replay",
      back: `Back to ${battle.plural}`,
    },
    results: {
      label: "Results",
      winner: "Winner",
      placeholder: (index) => `Round ${String(index + 1).padStart(2, "0")}`,
    },
    arcade: {
      name: (modeLabel) => `${modeLabel} mode ${battle.singular}`,
      cost: "Cost",
      roundsShort: "Rounds:",
      battleCost: `${capitalizeTerm(battle.singular)} cost`,
      fastSpin: "Fast spin",
      private: "Private",
      mute: "Mute",
      unmute: "Unmute",
      more: "More options",
      details: "Details",
      close: "Close",
      players: "Players",
      battleType: `${capitalizeTerm(battle.singular)} type`,
      freeForAll: "Free for all",
      seating: (players) => `${players} · ${players === 2 ? "1v1" : `${players}-way`}`,
      boxes: (count) => `${count} total ${count === 1 ? box.singular : box.plural}`,
      roundsOf: (count) => `×${count}`,
      waitingTab: "Waiting for players",
      startingTab: `${capitalizeTerm(battle.singular)} starting`,
      finishedTab: `${capitalizeTerm(battle.singular)} finished`,
      waiting: "Waiting",
      ready: "Ready",
      callAllBots: "Call all bots",
      startingIn: (count) => `Starting in ${count}`,
      go: "Go!",
      goSaid: "Go",
      tallying: (round, total) => `Adding up the pulls · Round ${round} of ${total}`,
      wins: (name, you) => `${name} ${you ? "win" : "wins"}`,
      tiedOnValue: (by) =>
        by === "seatOrder"
          ? "Tied on value and rolls · decided by seat order"
          : `Tied on value · the ${by === "higherRolls" ? "higher" : "lower"} rolls win`,
      winner: "Winner",
      recreate: `Recreate ${battle.singular}`,
      rewatch: "Rewatch",
      pulls: "Pulls",
      pullsOf: (name) => `${name}'s pulls`,
      noPulls: "No pulls yet",
      noWinnings: "No winnings",
      goTo: (name) => `Go to ${name}`,
    },
    fairness: {
      title: "Provably fair",
      show: "Show",
      hide: "Hide",
      caption: "Every revealed open",
      empty: "No rounds revealed yet",
      verification: "Raw verification data (JSON)",
    },
  };
}
